import { useFormContext } from "react-hook-form";
import { useEmailValidation } from "@circle-react/components/OnboardingPage/CreateProfilePage/useEmailValidation";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { isCard } from "@circle-react/helpers/paywallCheckoutHelpers";
import { CheckoutError } from "../CheckoutError";
import { PIX_MODAL_CLOSED, getReferral } from "../helpers";
import { useBuyNowPayLater } from "./paymentMethods/useBuyNowPayLater";
import { useCard } from "./paymentMethods/useCard";
import { usePix } from "./paymentMethods/usePix";
import { useHandleCheckoutError } from "./useHandleCheckoutError";

const didPixModalClose = (error: unknown) =>
  error instanceof Error &&
  "cause" in error &&
  typeof error.cause === "object" &&
  error.cause !== null &&
  "error_details" in error.cause &&
  error.cause.error_details === PIX_MODAL_CLOSED.error_details;

export const useOnSubmit = () => {
  const checkoutProps = usePaywallCheckoutContext();
  const {
    setIsProcessingPayment,
    stripePaymentMethodType,
    clearAlerts,
    currentUser,
    previewMutation,
  } = checkoutProps;
  const { handleCheckoutError } = useHandleCheckoutError();
  const cardHooks = useCard();
  const pixHooks = usePix();
  const buyNowPayLaterHooks = useBuyNowPayLater();

  const { validateEmail } = useEmailValidation({ shouldUseDebounce: false });
  const { setError, clearErrors, setFocus } = useFormContext();

  const paymentMethodHookStrategies: any = {
    pix: pixHooks,
    card: cardHooks,
    link: cardHooks,
    klarna: buyNowPayLaterHooks,
    afterpay_clearpay: buyNowPayLaterHooks,
    affirm: buyNowPayLaterHooks,
    google_pay: cardHooks,
    apple_pay: cardHooks,
    // EU Payment Methods
    sepa_debit: cardHooks,
    ideal: cardHooks,
    bancontact: cardHooks,
  };

  const performMailgunValidation = async (email: string) => {
    if (!currentUser && email) {
      clearErrors("email");
      const validationResult = await validateEmail(email);

      if (validationResult !== true) {
        setError("email", {
          type: "manual",
        });
        setFocus("email");
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (formData: any) => {
    formData.coupon_code =
      previewMutation?.variables?.queryParams?.coupon_code || "";

    clearAlerts();

    if (getReferral()) {
      formData.referral_metadata = getReferral();
    }

    const validPaymentMethodType = isCard(formData.payment_method_type)
      ? stripePaymentMethodType
      : formData.payment_method_type;

    if (!paymentMethodHookStrategies[validPaymentMethodType]) {
      throw new Error(`Invalid payment method type ${validPaymentMethodType}`);
    }

    const { onSubmitMutation } =
      paymentMethodHookStrategies[validPaymentMethodType];

    try {
      setIsProcessingPayment(true);

      const isEmailValid = await performMailgunValidation(formData.email);
      if (!isEmailValid) {
        return;
      }

      await onSubmitMutation.mutateAsync(formData);
    } catch (error: unknown) {
      if (didPixModalClose(error)) {
        // We do not report this "error" as it is expected behavior
      } else if (error instanceof CheckoutError) {
        handleCheckoutError(error);
      } else {
        handleCheckoutError(
          CheckoutError.fromUnknownError("useOnSubmit", error),
        );
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return {
    onSubmit,
  };
};
